import type { AppScreen } from '../lib/query.js';
import { LoadedImage } from './LoadedImage.js';

/** Which generated picture (public/art/bg-*.webp) sits behind each screen. Home has the GameBackdrop instead. */
const SCREEN_ART: Partial<Record<AppScreen, string>> = {
  home: 'hero-home',
  games: 'bg-games',
  game: 'bg-table',
  lobby: 'bg-lobby',
  learn: 'bg-learn',
  lesson: 'bg-learn',
  'online-lobby': 'bg-online',
  'online-game': 'bg-table',
  leaderboard: 'bg-leaderboard',
  parent: 'bg-parent',
  settings: 'bg-settings',
};

/**
 * A full-bleed picture behind a screen: the screen's art, washed with the page colour so text on the
 * translucent panels keeps its contrast. Fixed, ignores the pointer, hidden from screen readers.
 *
 * It is always in view, so it loads eagerly and at high priority (`loading="lazy"` only delayed it), and it says when it has
 * loaded (`data-loaded`): a screenshot taken before the art arrived and one taken after differ by a tenth of the screen, which
 * made the audit goldens flip between the two (the goldens bot saw one, CI the other).
 */
export function PageArt({ screen }: { screen: AppScreen }) {
  const art = SCREEN_ART[screen];
  if (!art) return null;
  return (
    <div aria-hidden="true" data-testid="page-art" data-art={art} className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <LoadedImage src={`/art/${art}.webp`} alt="" className="size-full object-cover opacity-60 dark:opacity-30" loading="eager" fetchPriority="high" decoding="async" />
      <div className="absolute inset-0 bg-gradient-to-b from-bg/30 via-bg/50 to-bg/80" />
    </div>
  );
}
